import { animate, state, style, transition, trigger } from "@angular/animations";
import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { EntityServices } from "@ngrx/data";
import {
	AlertService,
	AnalyticsService,
	Basics,
	Member,
	SessionService,
} from "@recore-solutions/nstar-lib";
import {
	BehaviorSubject,
	combineLatest,
	EMPTY,
	from,
	fromEvent,
	merge,
	Observable,
	Subject,
	Subscription,
	timer,
} from "rxjs";
import {
	catchError,
	distinctUntilChanged,
	exhaustMap,
	filter,
	map,
	pairwise,
	retry,
	share,
	switchMap,
	take,
	tap,
	throttleTime,
	timeout,
} from "rxjs/operators";
import { environment } from "src/environments/environment";
import { LoonEnvironmentName } from "src/environments/loon.environment";
import { Alert } from "./alert";
import { ConfigurationService } from "./configuration.service";
import { HomeComponent } from "./home/home.component";
import * as CacheBusters from "./shared/cache-busters";
import { Configuration } from "./shared/interfaces/configuration";
import { ZendeskWebwidgetService } from "./zendesk-webwidget.service";
import { Userpilot } from "userpilot";
import { initialize, TextCapture } from "@pendo/agent";
import { OAuthService } from "angular-oauth2-oidc";

enum VisibilityState {
	Visible = "visible",
	Hidden = "hidden",
}

const PENDO_DATA_HOST = "https://data.pendo.northstarmls.com";
const PENDO_CONTENT_HOST = "https://content.pendo.northstarmls.com";

@Component({
	selector: "app-root",
	templateUrl: "./app.component.html",
	styleUrls: ["./app.component.scss"],
	animations: [
		trigger("toggle", [
			state(
				VisibilityState.Hidden,
				style({ position: "sticky", top: 0, transform: "translateY(calc(1.25em - 100%))" })
				// style({ 'z-index': 100 })
			),
			state(
				VisibilityState.Visible,
				style({ position: "sticky", top: 0, transform: "translateY(0)" })
			),
			transition(VisibilityState.Hidden + "=>" + VisibilityState.Visible, [
				style({ position: "sticky", top: 0, transform: "translateY(calc(1.25em - 100%))" }),
				animate("200ms ease-in", style({ position: "sticky", top: 0, transform: "translateY(0)" })),
			]),
			transition(VisibilityState.Visible + "=> *", [
				style({ position: "sticky", top: 0, transform: "translateY(0)" }),
				animate(
					"200ms ease-in",
					style({ position: "sticky", top: 0, transform: "translateY(calc(1.25em - 100%))" })
				),
			]),
		]),
	],
})
export class AppComponent implements AfterViewInit, OnDestroy {
	title = "Add/Edit";
	sessionSecondsRemaining = 60 * 5;
	isVisible: boolean;
	subscriptions: Subscription[] = [];
	private showCoveredMemberSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
	showCoveredMember$: Observable<boolean> = this.showCoveredMemberSubject.asObservable();

	@ViewChild("stickyHeader") stickyHeader: ElementRef<any>;
	get threshold(): number {
		return this.stickyHeader.nativeElement.offsetHeight;
	}

	// @HostBinding('@toggle')
	get toggle(): VisibilityState {
		return this.isVisible ? VisibilityState.Visible : VisibilityState.Hidden;
	}
	scroll$: Observable<VisibilityState>;
	private mouseOverSubject: Subject<boolean> = new Subject();
	mouseOver$: Observable<boolean> = this.mouseOverSubject.asObservable();

	get config(): Configuration {
		return this.configurationService.config;
	}

	showCollectorDialog: () => any = () => {};

	constructor(
		private router: Router,
		private sessionService: SessionService,
		private alertService: AlertService,
		private zendeskWebwidgetService: ZendeskWebwidgetService,
		private analyticsService: AnalyticsService,
		private configurationService: ConfigurationService,
		private ngZone: NgZone,
		private entityServices: EntityServices,
		private oauthService: OAuthService
	) {
		if (!this.oauthService.hasValidAccessToken()) {
			this.oauthService.initCodeFlow();
		} else {
			this.analyticsService.start();
			this.sessionService.permissionLevel$.pipe(take(1), timeout(5000)).subscribe({
				next: () => {},
				error: () => this.router.navigate(["/", "error", "403"], { skipLocationChange: true }),
			});
			this.handleAlerts();
			this.handleZendeskWebWidget();
			this.prepareJiraForm();
			this.sessionService.userBasic$.pipe(take(1)).subscribe((user) => {
				if (user) {
					this.prepareUserPilot(user);
					this.preparePendo(user).catch((error) =>
						console.error("Failed to initialize Pendo", error)
					);
				}
			});
		}

		this.subscriptions.push(
			this.oauthService.events
				.pipe(
					filter((e) => e.type === "token_refresh_error" || e.type === "silent_refresh_error"),
					exhaustMap(() =>
						timer(5000).pipe(
							switchMap(() => from(this.oauthService.refreshToken())),
							retry(2),
							catchError(() => {
								// only log out after all retries are exhausted
								this.oauthService.stopAutomaticRefresh();
								this.oauthService.logOut();
								this.router.navigate(["/logout"]);
								return EMPTY;
							})
						)
					)
				)
				.subscribe()
		);
	}

	prepareUserPilot(user: Basics<Member>) {
		Userpilot.identify(user.memberReference, {
			name: user.memberFullName,
		});
	}

	async preparePendo(user: Basics<Member>) {
		const pendoApiKey = environment.pendo.apiKey;
		if (!pendoApiKey) {
			return;
		}

		const memberName = user.memberFullName.split(" ");

		await initialize({
			apiKey: pendoApiKey,
			env: "io",
			config: await this.getPendoConfig(pendoApiKey),
			dataHost: PENDO_DATA_HOST,
			contentHost: PENDO_CONTENT_HOST,
			visitor: {
				id: user.memberReference,
				fistName: memberName[0] || "",
				lastName: memberName[memberName.length - 1] || "",
			},
			globalKey: "pendo",
			plugins: [TextCapture],
		});
	}

	private async getPendoConfig(apiKey: string): Promise<Record<string, unknown>> {
		const configUrl = new URL(`agent/static/${apiKey}/config.json`, PENDO_CONTENT_HOST);
		const response = await fetch(configUrl);
		if (!response.ok) {
			throw new Error(`Failed to load Pendo config from ${configUrl.hostname}`);
		}

		return response.json();
	}

	ngAfterViewInit() {
		const scroll$ = fromEvent(window, "scroll").pipe(
			throttleTime(10),
			map(() => window.pageYOffset),
			pairwise(),
			map(([y1, y2]) =>
				y2 < this.threshold
					? VisibilityState.Visible
					: y2 < y1
					? VisibilityState.Visible
					: VisibilityState.Hidden
			)
		);
		this.scroll$ = merge(
			scroll$,
			combineLatest([
				this.mouseOver$.pipe(
					map((value) => (value ? VisibilityState.Visible : VisibilityState.Hidden))
				),
				scroll$,
			]).pipe(map(([hover, scroll]) => (scroll === VisibilityState.Visible ? scroll : hover)))
		).pipe(distinctUntilChanged(), share());
	}

	toggleHeader(value: boolean): void {
		this.mouseOverSubject.next(value);
	}

	prepareJiraForm(): void {
		(window as any).ATL_JQ_PAGE_PROPS = {
			triggerFunction: (showCollectorDialog) => {
				this.showCollectorDialog = showCollectorDialog;
			},
			fieldValues: {
				description:
					"\n\n\n------------\nEnvironment: \t\t" +
					environment.name +
					"\nLogged In User: \t" + // this.sessionService.userBasics?.memberReference +
					// '\nPermission Level: \t' + this.sessionService.session.sessionAttributes.loggedInUserAgentInfo.permissionLevel +
					"\n\nUser Agent: \t\t" +
					window.navigator.userAgent,
			},
		};
		const body = document.body as HTMLDivElement;
		const script = document.createElement("script");
		script.innerHTML = "";
		// tslint:disable-next-line: max-line-length
		script.src =
			"https://northstarmls.atlassian.net/s/d41d8cd98f00b204e9800998ecf8427e-T/8ur02a/b/23/a44af77267a987a660377e5c46e0fb64/_/download/batch/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector.js?locale=en-US&collectorId=310a5be5";
		script.async = false;
		script.defer = true;
		script.type = "text/javascript";
		body.appendChild(script);
	}

	openJiraForm = (): void => {
		this.showCollectorDialog();
	};

	handleAlerts(): void {
		this.handleMemberCoveredAlert();
		this.handleEnvironmentAlert();
	}

	handleMemberCoveredAlert(): void {
		this.subscriptions.push(
			combineLatest([
				this.showCoveredMember$,
				this.sessionService.isCovering$.pipe(distinctUntilChanged(), tap(this.clearCaches)),
			])
				.pipe(
					map(this.mapBooleansToEveryBoolean),
					distinctUntilChanged(),
					tap(this.toggleMemberCoveredAlert)
				)
				.subscribe()
		);
	}

	mapBooleansToEveryBoolean = (booleans: boolean[]) => booleans.every((b) => !!b);

	toggleMemberCoveredAlert = (show: boolean) => {
		if (show) {
			this.alertService.alertInfo(
				"Logged in as " + this.sessionService.memberBasics?.memberFullName,
				{
					collapsible: true,
					startCollapsed: true,
					collapseDelay: 0,
					permanent: true,
					bodyIcon: "agent",
					cssClass: "bg-impersonating text-light",
				},
				"impersonating"
			);
		} else {
			this.alertService.removeUniqueAlert("impersonating");
		}
	};

	handleEnvironmentAlert(): void {
		const envAlert = this.getEnvironmentAlert();
		if (!!envAlert) {
			this.alertService.addAlert({
				...envAlert,
				permanent: true,
				collapsible: true,
				startCollapsed: true,
				position: "top",
			});
		}
	}

	handleZendeskWebWidget(): void {
		this.zendeskWebwidgetService.hideWidget();
		this.zendeskWebwidgetService.onClose(this.zendeskWebwidgetService.hideWidget);
		this.sessionService.userBasic$
			.pipe(
				take(1),
				map(this.mapUserToPrefillObject),
				tap(this.zendeskWebwidgetService.prefillWidget)
			)
			.subscribe();
	}

	mapUserToPrefillObject = (user?: Basics<Member>) =>
		!!user ? { name: { value: `${user.memberFullName} (${user.memberReference})` } } : {};

	fmtMSS(s: any) {
		return (s - (s %= 60)) / 60 + (9 < s ? ":" : ":0") + s;
	}

	getEnvironmentAlert(environmentName: LoonEnvironmentName = environment.name): {
		body: string;
		cssClass: string;
		bodyIcon: string;
		clickBodyFn?: Alert["clickBodyFn"];
	} | null {
		switch (environmentName) {
			case "DEV":
				return {
					body: "Development",
					cssClass: "bg-success text-light mt-1",
					bodyIcon: "assets/images/envIconDev.svg",
					clickBodyFn: this.openJiraForm,
				};
			case "TEST":
			case "TEST-BK":
				return {
					body: "Testing",
					cssClass: "bg-warning text-light mt-1",
					bodyIcon: "assets/images/envIconTest.svg",
					clickBodyFn: this.openJiraForm,
				};
			case "STAGE":
			case "STAGE-BK":
				return {
					body: "Training",
					cssClass: "bg-danger text-light mt-1",
					bodyIcon: "assets/images/envIconTraining.svg",
				};
			default:
				return null;
		}
	}

	handleRouteActivation(component: any): void {
		this.showCoveredMemberSubject.next(!(component instanceof HomeComponent));
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach((s) => s.unsubscribe());
	}

	private clearCaches = (): void => {
		// // Legacy caches.
		CacheBusters.ownerCacheBuster$.next();
		CacheBusters.memberCacheBuster$.next();
		CacheBusters.loginCacheBuster$.next();
		CacheBusters.officeCacheBuster$.next();
		CacheBusters.listingCacheBuster$.next();
		CacheBusters.listingOptionsCacheBuster$.next();
		// localStorage.removeItem('CACHE_STORAGE');

		// // Polaris
		// Clear store as needed.
		const entityTypesToClear: string[] = [
			"Listing",
			"ListingSummary",
			"ListingPermission",
			"ListingServiceOffering",
		];
		entityTypesToClear.forEach(this.clearStore);
	};

	private clearStore = (type: string): void => {
		this.entityServices?.getEntityCollectionService(type)?.clearCache();
	};
}
